/**
 * The four contract/DocuSign edge-function calls, in one place (COL-533).
 *
 * V1's Dashboard.tsx grew four near-identical callers — same session lookup,
 * same fetch, same "read the body even on a 500 so the tenant sees why"
 * error unwrapping — differing only in which function they hit, which
 * fallback message they use, and which extra fields they carry off the error
 * body. V2's My room needs the same calls, and a second copy of a flow that
 * signs a legally binding contract is not something to own twice.
 *
 * Deliberately NOT a hook and NOT React-aware: these are four network calls.
 * Mutations, toasts, the DoB gate and the session-TTL banner stay with the
 * surface that renders them, because V1 and V2 present them differently.
 *
 * `fetch` rather than `supabase.functions.invoke`, exactly as V1 did: invoke
 * discards the response body on a non-2xx, and these functions put the
 * actionable part of the failure (`details`, `hint`, `code`) in that body.
 */

import { supabase, supabaseAnonKey, supabaseUrl } from "@/lib/supabase";

/** What the edge functions add to a plain Error, when they have it. */
export type ContractFunctionError = Error & {
  details?: string;
  hint?: string;
  code?: string;
};

type BodyErrorRule =
  /** Treat `body.error` as a failure even on a 2xx. */
  | "always"
  /** Only when no `url` came back — a warning alongside a usable URL is not. */
  | "whenNoUrl"
  /** Never: a 2xx is a success whatever the body says. */
  | "never";

type CallOptions = {
  /** Fallback when the body carries no `error` and there's no status text. */
  fallbackMessage: string;
  bodyErrorRule: BodyErrorRule;
  /** Extra fields lifted off the error body onto the thrown Error. */
  carry: ReadonlyArray<"details" | "hint" | "code">;
  /** Whether to send the `apikey` header. Only create-contract did. */
  sendApiKey?: boolean;
};

function fail(
  message: string,
  body: Record<string, unknown>,
  carry: CallOptions["carry"]
): never {
  const err = new Error(message) as ContractFunctionError;
  for (const field of carry) {
    const value = body?.[field];
    if (value != null) err[field] = String(value);
  }
  throw err;
}

async function callContractFunction(
  fn: string,
  bookingId: number,
  opts: CallOptions
): Promise<Record<string, unknown>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const base = (supabaseUrl || "").replace(/\/+$/, "");
  const res = await fetch(`${base}/functions/v1/${fn}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session?.access_token ?? ""}`,
      ...(opts.sendApiKey ? { apikey: supabaseAnonKey } : {}),
    },
    body: JSON.stringify({ bookingId }),
  });
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;

  if (!res.ok) {
    fail(
      String(body?.error || res.statusText || opts.fallbackMessage),
      body,
      opts.carry
    );
  }
  if (opts.bodyErrorRule !== "never" && body?.error) {
    const blockedByUrl = opts.bodyErrorRule === "whenNoUrl" && body?.url;
    if (!blockedByUrl) fail(String(body.error), body, opts.carry);
  }
  return body;
}

/**
 * Mark the booking signed after DocuSign redirects with
 * `event=signing_complete`. A 2xx is a success whatever the body holds.
 */
export function syncDocusignEnvelope(
  bookingId: number,
  fallbackMessage: string
) {
  return callContractFunction("docusign-sync-envelope", bookingId, {
    fallbackMessage,
    bodyErrorRule: "never",
    carry: ["details"],
  });
}

/** Fetch a fresh signing URL for an envelope that already exists. */
export function getDocusignSigningUrl(
  bookingId: number,
  fallbackMessage: string
) {
  return callContractFunction("get-docusign-signing-url", bookingId, {
    fallbackMessage,
    bodyErrorRule: "whenNoUrl",
    carry: ["details"],
  });
}

/**
 * Generate the contract PDF. The only one of the four that sends `apikey`;
 * kept as it was rather than normalised, because changing which headers
 * reach a deployed function is not a refactor.
 */
export function createContract(bookingId: number, fallbackMessage: string) {
  return callContractFunction("create-contract", bookingId, {
    fallbackMessage,
    bodyErrorRule: "always",
    carry: ["details", "hint", "code"],
    sendApiKey: true,
  });
}

/** Push the contract into DocuSign and come back with a signing URL. */
export function sendContractToDocusign(
  bookingId: number,
  fallbackMessage: string
) {
  return callContractFunction("send-contract-to-docusign", bookingId, {
    fallbackMessage,
    bodyErrorRule: "whenNoUrl",
    carry: ["details", "code"],
  });
}

/**
 * Open a DocuSign signing session.
 *
 * Returns true when it opened in a NEW tab, false when it took over this
 * one — the caller uses that to decide whether to warn first. On mobile a
 * new tab is never attempted: a signing session opened in a background tab
 * on a phone is a session the tenant cannot find.
 *
 * The popup-blocked fallback is the part worth sharing: a blocked
 * `window.open` returns a window that is null or already closed, and
 * without the fallback the tenant clicks "sign" and nothing whatsoever
 * happens.
 */
export function openSigningWindow(
  url: string,
  opts: {
    isMobile: boolean;
    onSameTab?: (reason: "mobile" | "blocked") => void;
  }
): boolean {
  if (opts.isMobile) {
    opts.onSameTab?.("mobile");
    window.location.href = url;
    return false;
  }
  const w = window.open(url, "_blank", "noopener,noreferrer");
  if (!w || w.closed) {
    opts.onSameTab?.("blocked");
    window.location.href = url;
    return false;
  }
  return true;
}
